import { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { clinicInfo } from '@/mocks/homeData';

export default function AboutSection() {
  const ref = useRef(null);
  const imageRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const { scrollYProgress: imageScroll } = useScroll({
    target: imageRef,
    offset: ['start end', 'end start'],
  });
  const imageParallax = useTransform(imageScroll, [0, 1], ['0%', '12%']);

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: (delay: number) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay, ease: [0.25, 0.1, 0.25, 1] },
    }),
  };

  return (
    <section ref={ref} className="relative bg-background-50 py-24 md:py-32 lg:py-40 overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-primary-50/40 -translate-y-1/2 translate-x-1/4 blur-3xl pointer-events-none" />

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Text Content — staggered */}
          <motion.div
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            variants={{
              visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
            }}
          >
            <motion.span
              variants={fadeInUp}
              custom={0}
              className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label"
            >
              Nossa História
            </motion.span>

            <motion.h2
              variants={fadeInUp}
              custom={0.1}
              className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground-950 mt-4 leading-[1.1]"
            >
              Uma clínica que<br />
              <span className="italic font-light">entende a mulher.</span>
            </motion.h2>

            <motion.p
              variants={fadeInUp}
              custom={0.2}
              className="mt-6 text-foreground-600 leading-relaxed text-sm md:text-base"
            >
              A Femme Vie nasceu do desejo de criar um espaço onde a saúde feminina fosse tratada com a profundidade e o cuidado que merece. Fundada pelas Dras. Nathália Posso e Mayna Moura, a clínica representa a união de duas visões complementares: a excelência técnica da medicina tradicional e a sensibilidade de uma abordagem verdadeiramente humanizada.
            </motion.p>

            <motion.p
              variants={fadeInUp}
              custom={0.3}
              className="mt-4 text-foreground-600 leading-relaxed text-sm md:text-base"
            >
              Localizada no coração do Cocó, em Fortaleza, nossa clínica foi projetada para ser um refúgio de bem-estar. Cada detalhe — da iluminação à escolha dos materiais — foi pensado para que você se sinta acolhida, segura e respeitada em cada consulta.
            </motion.p>

            {/* Values */}
            <motion.div
              variants={fadeInUp}
              custom={0.4}
              className="grid grid-cols-3 gap-6 mt-10"
            >
              {[
                { label: 'Missão', text: 'Cuidar da saúde feminina com excelência, empatia e inovação' },
                { label: 'Visão', text: 'Ser referência em saúde da mulher no Nordeste' },
                { label: 'Valores', text: 'Humanização, ética, tecnologia e acolhimento' },
              ].map((item) => (
                <div key={item.label}>
                  <span className="text-xs tracking-[0.12em] uppercase text-primary-400 font-label whitespace-nowrap">
                    {item.label}
                  </span>
                  <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed">
                    {item.text}
                  </p>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Image with parallax */}
          <motion.div
            ref={imageRef}
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            className="relative"
          >
            <div className="relative aspect-[3/4] overflow-hidden rounded-lg">
              <motion.img
                style={{ y: imageParallax }}
                src="https://storage.readdy-site.link/project_files/ed85e06b-2b23-49e7-a477-ed0f88fc71a8/96e71a8b-44c9-4a17-8547-9562d98a2325_compressed_Captura-de-tela-2026-07-22-154607.webp"
                alt="Interior da Clínica Femme Vie"
                className="w-full h-[120%] object-cover object-top"
              />
            </div>
            {/* Decorative frame */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="absolute -bottom-6 -right-6 w-full h-full border border-primary-200/40 rounded-lg -z-10"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}